import { ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { InvestmentFinancialMetricsDTO } from '@inversor-hub/types';
import { formatCurrency, formatPercentage } from '@/utils/misc';
import { EstimateChip } from './EstimateChip';
import { metricValue } from '../shared/metric-value';
import { cardSurface } from '../shared/surfaces';

interface FinancingPanelProps {
  investmentId: string;
  metrics: InvestmentFinancialMetricsDTO;
  scaleAmount: (cents: number) => number;
}

const ESTIMATED_HELP =
  'Se calcula con un cuadro de amortización francés a partir del importe, el plazo y el tipo que guardaste en la ficha. No tiene en cuenta amortizaciones anticipadas ni revisiones del Euríbor.';

const Stat = ({ label, help, children }: { label: string; help?: string; children: ReactNode }) => (
  <Box>
    <Typography
      variant="overline"
      color="text.secondary"
      display="block"
      lineHeight={1.4}
      sx={{ minHeight: 34 }}
    >
      {label}
      {help && (
        <Tooltip title={help} arrow>
          <InfoOutlinedIcon
            fontSize="inherit"
            sx={{ ml: 0.5, color: 'text.secondary', cursor: 'help', verticalAlign: 'middle' }}
          />
        </Tooltip>
      )}
    </Typography>
    {children}
  </Box>
);

export const FinancingPanel = ({ investmentId, metrics, scaleAmount }: FinancingPanelProps) => {
  const { financing } = metrics;

  if (!financing) {
    return (
      <Paper elevation={0} sx={{ p: 3, height: '100%', ...cardSurface }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Financiación y deuda
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Alert
          severity="info"
          action={
            <Button
              size="small"
              component={RouterLink}
              to={`/inversiones/${investmentId}/editar`}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Completar
            </Button>
          }
        >
          Para estimar el capital pendiente y la cuota hacen falta el importe del préstamo, la fecha
          de compra, el plazo y el tipo de interés.
        </Alert>
      </Paper>
    );
  }

  const repaid = financing.originalLoanAmount - financing.outstandingBalance;
  const repaidPct =
    financing.originalLoanAmount > 0 ? (repaid / financing.originalLoanAmount) * 100 : 0;
  const interestPaid = scaleAmount(metrics.metrics.interestPaid.value ?? 0);
  const principalRepaid = scaleAmount(metrics.metrics.principalRepaid.value ?? 0);
  const debtNotRecorded = interestPaid === 0 && principalRepaid === 0;
  const { actualAnnualized, annualEstimate } = metrics;
  const ytdInterest = metricValue(actualAnnualized?.interestPaid);
  const ytdPrincipal = metricValue(actualAnnualized?.principalRepaid);
  const fichaInterest = metricValue(annualEstimate?.interestPaid);
  const fichaPrincipal = metricValue(annualEstimate?.principalRepaid);

  return (
    <Paper elevation={0} sx={{ p: 3, height: '100%', ...cardSurface }}>
      <Typography variant="h6" fontWeight={600}>
        Financiación y deuda
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cómo va el préstamo y qué parte del año se ha ido en cuota.
      </Typography>
      <Divider sx={{ mb: 2.5 }} />

      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" fontWeight={600}>
              Capital pendiente
            </Typography>
            <EstimateChip label="Estimado" help={ESTIMATED_HELP} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {formatCurrency(scaleAmount(financing.outstandingBalance))}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(Math.max(repaidPct, 0), 100)}
          color="primary"
          sx={{ height: 8, borderRadius: 4 }}
          aria-label={`Amortizado el ${repaidPct.toFixed(1)} % del préstamo`}
        />
        <Typography variant="caption" color="text.secondary">
          Amortizado {repaidPct.toFixed(1)} % de{' '}
          {formatCurrency(scaleAmount(financing.originalLoanAmount))}
        </Typography>
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={6} sm={3}>
          <Stat label="Tipo de interés" help="Tipo guardado en la ficha de la inversión.">
            <Typography variant="h6" fontWeight={700}>
              {formatPercentage(financing.annualRate)}
            </Typography>
          </Stat>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Stat label="Cuota mensual" help={ESTIMATED_HELP}>
            <Typography variant="h6" fontWeight={700}>
              {formatCurrency(scaleAmount(financing.estimatedMonthlyPayment))}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatCurrency(scaleAmount(financing.estimatedMonthlyPayment * 12))} / año
            </Typography>
          </Stat>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Stat
            label="Intereses pagados"
            help="Movimientos registrados con categoría «Intereses de hipoteca/préstamo» y fecha dentro del periodo."
          >
            <Typography variant="h6" fontWeight={700}>
              {formatCurrency(interestPaid)}
            </Typography>
            {ytdInterest !== null ? (
              <Typography variant="caption" color="text.secondary" display="block">
                Ritmo YTD {formatCurrency(scaleAmount(ytdInterest))}
              </Typography>
            ) : null}
            {fichaInterest !== null ? (
              <Typography variant="caption" color="text.secondary" display="block">
                Previsión ficha {formatCurrency(scaleAmount(fichaInterest))}
              </Typography>
            ) : debtNotRecorded ? (
              <Box display="flex" alignItems="center" gap={0.75} mt={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {formatCurrency(scaleAmount(financing.estimatedAnnualInterest))}
                </Typography>
                <EstimateChip label="Estimado" help={ESTIMATED_HELP} />
              </Box>
            ) : null}
          </Stat>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Stat
            label="Principal amortizado"
            help="Movimientos registrados con categoría «Devolución de principal del préstamo» y fecha dentro del periodo."
          >
            <Typography variant="h6" fontWeight={700}>
              {formatCurrency(principalRepaid)}
            </Typography>
            {ytdPrincipal !== null ? (
              <Typography variant="caption" color="text.secondary" display="block">
                Ritmo YTD {formatCurrency(scaleAmount(ytdPrincipal))}
              </Typography>
            ) : null}
            {fichaPrincipal !== null ? (
              <Typography variant="caption" color="text.secondary" display="block">
                Previsión ficha {formatCurrency(scaleAmount(fichaPrincipal))}
              </Typography>
            ) : debtNotRecorded ? (
              <Box display="flex" alignItems="center" gap={0.75} mt={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {formatCurrency(scaleAmount(financing.estimatedAnnualPrincipal))}
                </Typography>
                <EstimateChip label="Estimado" help={ESTIMATED_HELP} />
              </Box>
            ) : null}
          </Stat>
        </Grid>
      </Grid>

      {debtNotRecorded && (
        <Alert severity="info" sx={{ mt: 2.5 }}>
          No hay movimientos de hipoteca registrados en este periodo, así que el desglose que ves
          debajo de cada importe es una estimación del cuadro de amortización. Para que cuente como
          real, registra la cuota en Transacciones separando intereses y principal.
        </Alert>
      )}
    </Paper>
  );
};
